<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';
import LaisMetricStrip from 'dashboard/components-next/LaisMetricStrip.vue';
import LaisPayloadViewer from 'dashboard/components-next/LaisPayloadViewer.vue';
import LaisRedactedField from 'dashboard/components-next/LaisRedactedField.vue';
import LaisStatusBadge from 'dashboard/components-next/LaisStatusBadge.vue';
import CaptainAssistantAPI from 'dashboard/api/captain/assistant';
import AgentNotice from './AgentNotice.vue';
import AgentSection from './AgentSection.vue';
import {
  buildDlqItems,
  buildHandoffPlanSummary,
  buildOperationMetrics,
  buildReadinessItems,
  buildReplayPlanSummary,
  buildSloItems,
  operationStatusTone,
  redactedViewerKeys,
  valueAt,
} from '../agentOperations';

const props = defineProps({
  assistantId: {
    type: [Number, String],
    required: true,
  },
  runs: {
    type: Array,
    default: () => [],
  },
  canManage: {
    type: Boolean,
    default: false,
  },
  api: {
    type: Object,
    default: () => CaptainAssistantAPI,
  },
});

const { t: ce } = useI18n();

const snapshot = ref(null);
const loadError = ref('');
const isLoading = ref(false);
const selectedWindow = ref(1440);
const replayPlansByRunId = ref({});
const replayErrorsByRunId = ref({});
const replayLoadingRunId = ref(null);
const handoffPlansByRunId = ref({});
const handoffErrorsByRunId = ref({});
const handoffLoadingRunId = ref(null);

let snapshotRequestId = 0;

const windowOptions = computed(() => [
  { value: 60, label: ce('CAPTAIN_EXPERIENCE.OPERATIONS.WINDOW_1H') },
  { value: 1440, label: ce('CAPTAIN_EXPERIENCE.OPERATIONS.WINDOW_24H') },
  { value: 10080, label: ce('CAPTAIN_EXPERIENCE.OPERATIONS.WINDOW_7D') },
]);

const metricItems = computed(() =>
  buildOperationMetrics(snapshot.value || {}, ce)
);
const readinessItems = computed(() =>
  buildReadinessItems(snapshot.value?.readiness || {}, ce)
);
const sloItems = computed(() => buildSloItems(snapshot.value?.slo || {}, ce));
const dlqItems = computed(() =>
  buildDlqItems(snapshot.value || {}, replayPlansByRunId.value)
);
const circuitItems = computed(() =>
  valueAt(snapshot.value, ['metrics', 'circuits', 'items'], [])
);
const alertItems = computed(() =>
  valueAt(snapshot.value, ['alerts', 'items'], [])
);
const evalChecks = computed(() =>
  valueAt(snapshot.value, ['evals', 'checks'], {})
);
const evalCheckItems = computed(() =>
  Object.entries(evalChecks.value || {}).map(([key, value]) => ({
    key,
    passed: value?.passed === true,
    detail: value?.detail || '',
  }))
);
const solicitacoesSnapshot = computed(() =>
  valueAt(snapshot.value, ['domain_dependencies', 'solicitacoes_group_polling'])
);
const solicitacoesStatus = computed(
  () => solicitacoesSnapshot.value?.status || 'no_data'
);
const solicitacoesGroups = computed(() =>
  valueAt(solicitacoesSnapshot.value, ['authorized_groups'], [])
);
const solicitacoesLatestMessages = computed(() =>
  valueAt(solicitacoesSnapshot.value, ['latest_messages'], [])
);
const solicitacoesPolling = computed(
  () => solicitacoesSnapshot.value?.polling || {}
);
const solicitacoesLocks = computed(
  () => solicitacoesSnapshot.value?.locks || {}
);
const solicitacoesDeliveries = computed(
  () => solicitacoesSnapshot.value?.deliveries || {}
);
const snapshotStatus = computed(() => snapshot.value?.status || 'no_data');
const alertsStatus = computed(() =>
  valueAt(snapshot.value, ['alerts', 'status'], 'quiet')
);
const evalStatus = computed(() =>
  valueAt(snapshot.value, ['evals', 'status'], 'skipped')
);
const canLoadSnapshot = computed(() =>
  Boolean(props.assistantId && props.canManage)
);

const replayCandidateRuns = computed(() =>
  props.runs
    .filter(run => ['failed', 'running'].includes(run.status))
    .slice(0, 8)
);

const visibleReplayItems = computed(() => {
  const indexed = new Map(dlqItems.value.map(item => [item.run_id, item]));
  replayCandidateRuns.value.forEach(run => {
    if (indexed.has(run.id)) return;
    indexed.set(run.id, {
      run_id: run.id,
      status: run.status,
      eligible: false,
      reason:
        run.error_message ||
        ce('CAPTAIN_EXPERIENCE.OPERATIONS.REPLAY_NOT_IN_DLQ'),
      retry_mode: 'manual_review_only',
      currentPlan: replayPlansByRunId.value[run.id] || null,
      tone: replayPlansByRunId.value[run.id]
        ? operationStatusTone(replayPlansByRunId.value[run.id].retry_mode)
        : 'neutral',
    });
  });
  return Array.from(indexed.values());
});

const loadSnapshot = async () => {
  const targetAssistantId = Number(props.assistantId);
  snapshotRequestId += 1;
  const currentRequestId = snapshotRequestId;

  if (!targetAssistantId || !props.canManage) {
    snapshot.value = null;
    loadError.value = '';
    return;
  }

  isLoading.value = true;
  loadError.value = '';
  try {
    const { data } = await props.api.observability({
      assistantId: targetAssistantId,
      windowMinutes: selectedWindow.value,
      includeEval: true,
    });
    if (currentRequestId !== snapshotRequestId) return;
    snapshot.value = data.payload || {};
  } catch (error) {
    if (currentRequestId !== snapshotRequestId) return;
    loadError.value =
      error?.message || ce('CAPTAIN_EXPERIENCE.OPERATIONS.LOAD_ERROR');
  } finally {
    if (currentRequestId === snapshotRequestId) isLoading.value = false;
  }
};

const loadReplayPlan = async runId => {
  const targetAssistantId = Number(props.assistantId);
  if (!targetAssistantId || !runId || !props.canManage) return;

  replayLoadingRunId.value = runId;
  replayErrorsByRunId.value = {
    ...replayErrorsByRunId.value,
    [runId]: '',
  };

  try {
    const { data } = await props.api.replayPlan({
      assistantId: targetAssistantId,
      runId,
    });
    replayPlansByRunId.value = {
      ...replayPlansByRunId.value,
      [runId]: data.payload || {},
    };
  } catch (error) {
    replayErrorsByRunId.value = {
      ...replayErrorsByRunId.value,
      [runId]:
        error?.message || ce('CAPTAIN_EXPERIENCE.OPERATIONS.REPLAY_ERROR'),
    };
    useAlert(
      error?.message || ce('CAPTAIN_EXPERIENCE.OPERATIONS.REPLAY_ERROR')
    );
  } finally {
    if (replayLoadingRunId.value === runId) replayLoadingRunId.value = null;
  }
};

const loadHandoffPlan = async runId => {
  const targetAssistantId = Number(props.assistantId);
  if (!targetAssistantId || !runId || !props.canManage) return;

  handoffLoadingRunId.value = runId;
  handoffErrorsByRunId.value = {
    ...handoffErrorsByRunId.value,
    [runId]: '',
  };

  try {
    const { data } = await props.api.handoffPlan({
      assistantId: targetAssistantId,
      runId,
    });
    handoffPlansByRunId.value = {
      ...handoffPlansByRunId.value,
      [runId]: data.payload || {},
    };
  } catch (error) {
    handoffErrorsByRunId.value = {
      ...handoffErrorsByRunId.value,
      [runId]:
        error?.message || ce('CAPTAIN_EXPERIENCE.OPERATIONS.HANDOFF_ERROR'),
    };
    useAlert(
      error?.message || ce('CAPTAIN_EXPERIENCE.OPERATIONS.HANDOFF_ERROR')
    );
  } finally {
    if (handoffLoadingRunId.value === runId) handoffLoadingRunId.value = null;
  }
};

const statusLabel = status =>
  ce(
    `CAPTAIN_EXPERIENCE.OPERATIONS.STATUS_${String(status || 'unknown').toUpperCase()}`
  );

const boolTone = value => (value ? 'positive' : 'danger');
const planSummary = plan => buildReplayPlanSummary(plan, ce);
const handoffPlanSummary = plan => buildHandoffPlanSummary(plan, ce);
const boolLabel = value =>
  value
    ? ce('CAPTAIN_EXPERIENCE.OPERATIONS.YES')
    : ce('CAPTAIN_EXPERIENCE.OPERATIONS.NO');

watch(
  [() => props.assistantId, selectedWindow, () => props.canManage],
  () => {
    replayPlansByRunId.value = {};
    replayErrorsByRunId.value = {};
    handoffPlansByRunId.value = {};
    handoffErrorsByRunId.value = {};
    loadSnapshot();
  },
  { immediate: true }
);
</script>

<template>
  <section id="agent-operations" class="flex flex-col gap-6" tabindex="-1">
    <AgentNotice
      v-if="!canManage"
      :title="ce('CAPTAIN_EXPERIENCE.OPERATIONS.READ_ONLY_TITLE')"
      :description="ce('CAPTAIN_EXPERIENCE.OPERATIONS.READ_ONLY_DESCRIPTION')"
      tone="info"
    />

    <AgentSection
      :title="ce('CAPTAIN_EXPERIENCE.OPERATIONS.TITLE')"
      prefix-icon="i-lucide-gauge"
    >
      <template #titleAction>
        <div class="flex flex-wrap items-center justify-end gap-2">
          <label class="sr-only" for="captain-operations-window">
            {{ ce('CAPTAIN_EXPERIENCE.OPERATIONS.WINDOW_LABEL') }}
          </label>
          <select
            id="captain-operations-window"
            v-model.number="selectedWindow"
            class="h-9 rounded-lg border border-n-weak bg-n-solid-2 px-3 text-sm text-n-slate-12 outline-none transition hover:border-n-strong focus:border-n-brand focus:ring-2 focus:ring-n-brand/20"
          >
            <option
              v-for="option in windowOptions"
              :key="option.value"
              :value="option.value"
            >
              {{ option.label }}
            </option>
          </select>
          <Button
            type="button"
            color="slate"
            variant="faded"
            size="sm"
            icon="i-lucide-refresh-cw"
            :label="ce('CAPTAIN_EXPERIENCE.WORKSPACE.RETRY')"
            :is-loading="isLoading"
            :disabled="!canLoadSnapshot"
            @click="loadSnapshot"
          />
        </div>
      </template>

      <div class="flex flex-col gap-5">
        <AgentNotice
          :title="ce('CAPTAIN_EXPERIENCE.OPERATIONS.DRY_RUN_NOTICE_TITLE')"
          :description="
            ce('CAPTAIN_EXPERIENCE.OPERATIONS.DRY_RUN_NOTICE_DESCRIPTION')
          "
          tone="info"
        />

        <AgentNotice
          v-if="loadError"
          :title="ce('CAPTAIN_EXPERIENCE.OPERATIONS.LOAD_ERROR_TITLE')"
          :description="loadError"
          tone="error"
          action-icon="i-lucide-refresh-cw"
          :action-label="ce('CAPTAIN_EXPERIENCE.WORKSPACE.RETRY')"
          :is-loading="isLoading"
          @action="loadSnapshot"
        />

        <div
          class="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-n-weak bg-n-solid-2 p-4"
        >
          <div class="flex flex-wrap items-center gap-2">
            <LaisStatusBadge
              :label="statusLabel(snapshotStatus)"
              :tone="operationStatusTone(snapshotStatus)"
              icon="i-lucide-activity"
            />
            <LaisStatusBadge
              :label="statusLabel(alertsStatus)"
              :tone="operationStatusTone(alertsStatus)"
              icon="i-lucide-bell"
            />
            <LaisStatusBadge
              :label="statusLabel(evalStatus)"
              :tone="operationStatusTone(evalStatus)"
              icon="i-lucide-clipboard-check"
            />
          </div>
          <p class="text-xs text-n-slate-10">
            {{
              ce('CAPTAIN_EXPERIENCE.OPERATIONS.GENERATED_AT', {
                date: snapshot?.generated_at || '—',
              })
            }}
          </p>
        </div>

        <LaisMetricStrip
          :items="metricItems"
          :loading="isLoading && !snapshot"
          :columns="0"
          :aria-label="ce('CAPTAIN_EXPERIENCE.OPERATIONS.METRICS_ARIA')"
        />
      </div>
    </AgentSection>

    <AgentSection
      :title="ce('CAPTAIN_EXPERIENCE.OPERATIONS.READINESS_TITLE')"
      prefix-icon="i-lucide-shield"
    >
      <dl class="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
        <div
          v-for="item in readinessItems"
          :key="item.key"
          class="min-h-24 rounded-lg border border-n-weak bg-n-solid-2 p-4"
        >
          <dt class="text-xs font-medium uppercase text-n-slate-10">
            {{ item.label }}
          </dt>
          <dd class="mt-3">
            <LaisStatusBadge :label="item.value" :tone="item.tone" dot />
          </dd>
        </div>
      </dl>
    </AgentSection>

    <AgentSection
      :title="ce('CAPTAIN_EXPERIENCE.OPERATIONS.SOLICITACOES_TITLE')"
      prefix-icon="i-lucide-message-circle"
    >
      <div class="flex flex-col gap-4">
        <div class="flex flex-wrap items-center gap-2">
          <LaisStatusBadge
            :label="statusLabel(solicitacoesStatus)"
            :tone="operationStatusTone(solicitacoesStatus)"
            icon="i-lucide-activity"
          />
          <LaisStatusBadge
            :label="
              ce('CAPTAIN_EXPERIENCE.OPERATIONS.SOLICITACOES_NATIVE', {
                value: boolLabel(solicitacoesPolling.native_enabled),
              })
            "
            :tone="boolTone(solicitacoesPolling.native_enabled)"
            size="sm"
            dot
          />
          <LaisStatusBadge
            :label="
              ce('CAPTAIN_EXPERIENCE.OPERATIONS.SOLICITACOES_SEND_REAL', {
                value: boolLabel(solicitacoesPolling.send_real),
              })
            "
            :tone="solicitacoesPolling.send_real ? 'warning' : 'neutral'"
            size="sm"
            dot
          />
        </div>

        <dl class="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
          <div class="min-w-0 rounded-lg border border-n-weak bg-n-solid-2 p-4">
            <dt class="text-xs font-medium uppercase text-n-slate-10">
              {{ ce('CAPTAIN_EXPERIENCE.OPERATIONS.SOLICITACOES_POLLING') }}
            </dt>
            <dd class="mt-2 text-sm text-n-slate-12">
              {{ solicitacoesPolling.last_polled_at || '—' }}
            </dd>
            <p class="mt-1 text-xs text-n-slate-10">
              {{
                ce('CAPTAIN_EXPERIENCE.OPERATIONS.SOLICITACOES_POLLING_META', {
                  checked: solicitacoesPolling.last_checked || 0,
                  processed: solicitacoesPolling.last_processed || 0,
                  responded: solicitacoesPolling.last_responded || 0,
                })
              }}
            </p>
          </div>
          <div class="min-w-0 rounded-lg border border-n-weak bg-n-solid-2 p-4">
            <dt class="text-xs font-medium uppercase text-n-slate-10">
              {{ ce('CAPTAIN_EXPERIENCE.OPERATIONS.SOLICITACOES_LOCK') }}
            </dt>
            <dd class="mt-2">
              <LaisStatusBadge
                :label="statusLabel(solicitacoesLocks.last_status)"
                :tone="operationStatusTone(solicitacoesLocks.last_status)"
                size="sm"
                dot
              />
            </dd>
            <p class="mt-1 text-xs text-n-slate-10">
              {{
                ce('CAPTAIN_EXPERIENCE.OPERATIONS.SOLICITACOES_LOCK_META', {
                  active: solicitacoesLocks.active_processing_count || 0,
                  stale: solicitacoesLocks.stale_processing_count || 0,
                })
              }}
            </p>
          </div>
          <div class="min-w-0 rounded-lg border border-n-weak bg-n-solid-2 p-4">
            <dt class="text-xs font-medium uppercase text-n-slate-10">
              {{ ce('CAPTAIN_EXPERIENCE.OPERATIONS.SOLICITACOES_DELIVERY') }}
            </dt>
            <dd class="mt-2 text-sm text-n-slate-12">
              {{
                ce('CAPTAIN_EXPERIENCE.OPERATIONS.SOLICITACOES_DELIVERY_META', {
                  sent: solicitacoesDeliveries.sent_count || 0,
                  shadowed: solicitacoesDeliveries.shadowed_count || 0,
                  failed: solicitacoesDeliveries.failed_count || 0,
                })
              }}
            </dd>
            <p class="mt-1 text-xs text-n-slate-10">
              {{
                ce('CAPTAIN_EXPERIENCE.OPERATIONS.SOLICITACOES_UNKNOWN_META', {
                  count: solicitacoesDeliveries.delivery_unknown_count || 0,
                })
              }}
            </p>
          </div>
          <div class="min-w-0 rounded-lg border border-n-weak bg-n-solid-2 p-4">
            <dt class="text-xs font-medium uppercase text-n-slate-10">
              {{ ce('CAPTAIN_EXPERIENCE.OPERATIONS.SOLICITACOES_RECORDS') }}
            </dt>
            <dd class="mt-2 text-sm font-semibold text-n-slate-12">
              {{
                valueAt(
                  solicitacoesSnapshot,
                  ['operational_records', 'window_count'],
                  0
                )
              }}
            </dd>
            <p class="mt-1 text-xs text-n-slate-10">
              {{
                valueAt(
                  solicitacoesSnapshot,
                  ['operational_records', 'source_table'],
                  '—'
                )
              }}
            </p>
          </div>
        </dl>

        <div class="grid grid-cols-1 gap-3 lg:grid-cols-2">
          <article
            v-for="group in solicitacoesGroups"
            :key="`${group.role}-${group.jid}`"
            class="min-w-0 rounded-lg border border-n-weak bg-n-solid-2 p-4"
          >
            <div class="flex flex-wrap items-center justify-between gap-2">
              <p class="min-w-0 truncate text-sm font-medium text-n-slate-12">
                {{ group.subject || group.jid }}
              </p>
              <LaisStatusBadge :label="group.role" tone="info" size="sm" dot />
            </div>
            <p class="mt-2 truncate text-xs text-n-slate-10">
              {{ group.jid || '—' }}
            </p>
          </article>
        </div>

        <div
          v-if="solicitacoesLatestMessages.length"
          class="flex flex-col gap-3"
        >
          <article
            v-for="item in solicitacoesLatestMessages"
            :key="item.id"
            class="rounded-lg border border-n-weak bg-n-solid-2 p-4"
          >
            <div class="flex flex-wrap items-start justify-between gap-3">
              <div class="min-w-0">
                <div class="flex flex-wrap items-center gap-2">
                  <p class="text-sm font-medium text-n-slate-12">
                    {{
                      ce('CAPTAIN_EXPERIENCE.OPERATIONS.SOLICITACOES_MESSAGE', {
                        id: item.message_id || item.id,
                      })
                    }}
                  </p>
                  <LaisStatusBadge
                    :label="statusLabel(item.status)"
                    :tone="operationStatusTone(item.status)"
                    size="sm"
                    dot
                  />
                </div>
                <p class="mt-1 text-xs text-n-slate-10">
                  {{ item.group_subject || item.group_jid || '—' }}
                </p>
              </div>
              <p class="text-xs text-n-slate-10">
                {{ item.message_at || '—' }}
              </p>
            </div>

            <p v-if="item.body_preview" class="mt-3 text-sm text-n-slate-11">
              {{ item.body_preview }}
            </p>
            <p
              v-if="item.assistant_response_preview"
              class="mt-2 text-sm text-n-slate-12"
            >
              {{ item.assistant_response_preview }}
            </p>

            <dl class="mt-3 grid grid-cols-1 gap-2 md:grid-cols-3">
              <div class="rounded-lg bg-n-alpha-2 p-3">
                <dt class="text-xs font-medium uppercase text-n-slate-10">
                  {{ ce('CAPTAIN_EXPERIENCE.OPERATIONS.SOLICITACOES_SENT') }}
                </dt>
                <dd class="mt-1 text-sm text-n-slate-12">
                  {{ item.messages_sent || 0 }}
                </dd>
              </div>
              <div class="rounded-lg bg-n-alpha-2 p-3">
                <dt class="text-xs font-medium uppercase text-n-slate-10">
                  {{ ce('CAPTAIN_EXPERIENCE.OPERATIONS.SOLICITACOES_FAILED') }}
                </dt>
                <dd class="mt-1 text-sm text-n-slate-12">
                  {{ item.messages_failed || 0 }}
                </dd>
              </div>
              <div class="rounded-lg bg-n-alpha-2 p-3">
                <dt class="text-xs font-medium uppercase text-n-slate-10">
                  {{
                    ce('CAPTAIN_EXPERIENCE.OPERATIONS.SOLICITACOES_TRANSPORT')
                  }}
                </dt>
                <dd class="mt-1 break-words text-sm text-n-slate-12">
                  {{ item.delivery_state?.transport || '—' }}
                </dd>
              </div>
            </dl>

            <LaisPayloadViewer
              class="mt-4"
              :title="
                ce('CAPTAIN_EXPERIENCE.OPERATIONS.SOLICITACOES_PAYLOAD_TITLE')
              "
              :payload="{
                delivery_state: item.delivery_state,
                tool_payload_redacted: item.tool_payload_redacted,
                operational_trace: item.operational_trace,
              }"
              :redacted-keys="redactedViewerKeys"
              :can-copy="false"
              :max-depth="2"
            />
          </article>
        </div>
        <p v-else class="text-sm text-n-slate-11">
          {{ ce('CAPTAIN_EXPERIENCE.OPERATIONS.SOLICITACOES_EMPTY') }}
        </p>
      </div>
    </AgentSection>

    <AgentSection
      :title="ce('CAPTAIN_EXPERIENCE.OPERATIONS.SLO_TITLE')"
      prefix-icon="i-lucide-list-checks"
    >
      <div class="overflow-hidden rounded-lg border border-n-weak">
        <div
          v-for="item in sloItems"
          :key="item.key"
          class="grid gap-3 border-b border-n-weak bg-n-solid-2 p-4 last:border-b-0 md:grid-cols-[1fr_auto_auto]"
        >
          <div class="min-w-0">
            <p class="text-sm font-medium text-n-slate-12">{{ item.label }}</p>
            <p class="mt-1 text-xs text-n-slate-10">
              {{
                ce('CAPTAIN_EXPERIENCE.OPERATIONS.SLO_TARGET', {
                  target: item.target,
                  direction: item.direction,
                })
              }}
            </p>
          </div>
          <p class="text-sm font-semibold tabular-nums text-n-slate-12">
            {{ item.value }}
          </p>
          <LaisStatusBadge
            :label="statusLabel(item.status)"
            :tone="item.tone"
            dot
          />
        </div>
      </div>
    </AgentSection>

    <AgentSection
      :title="ce('CAPTAIN_EXPERIENCE.OPERATIONS.DLQ_TITLE')"
      prefix-icon="i-lucide-inbox"
    >
      <div v-if="visibleReplayItems.length" class="flex flex-col gap-3">
        <article
          v-for="item in visibleReplayItems"
          :key="item.run_id"
          class="rounded-lg border border-n-weak bg-n-solid-2 p-4"
        >
          <div class="flex flex-wrap items-start justify-between gap-3">
            <div class="min-w-0">
              <div class="flex flex-wrap items-center gap-2">
                <p class="text-sm font-medium text-n-slate-12">
                  {{
                    ce('CAPTAIN_EXPERIENCE.OPERATIONS.RUN_LABEL', {
                      id: item.run_id,
                    })
                  }}
                </p>
                <LaisStatusBadge
                  :label="item.status"
                  :tone="operationStatusTone(item.status)"
                  size="sm"
                  dot
                />
                <LaisStatusBadge
                  :label="
                    item.currentPlan?.operator_contract?.dry_run
                      ? ce('CAPTAIN_EXPERIENCE.OPERATIONS.DRY_RUN')
                      : ce('CAPTAIN_EXPERIENCE.OPERATIONS.PREVIEW_ONLY')
                  "
                  tone="info"
                  size="sm"
                  icon="i-lucide-eye"
                />
              </div>
              <p class="mt-1 text-xs text-n-slate-10">
                {{ item.currentPlan?.reason || item.reason }}
              </p>
            </div>
            <div class="flex flex-wrap items-center justify-end gap-2">
              <Button
                type="button"
                color="slate"
                variant="faded"
                size="sm"
                icon="i-lucide-file-search"
                :label="ce('CAPTAIN_EXPERIENCE.OPERATIONS.REPLAY_PLAN_ACTION')"
                :is-loading="replayLoadingRunId === item.run_id"
                @click="loadReplayPlan(item.run_id)"
              />
              <Button
                type="button"
                color="slate"
                variant="faded"
                size="sm"
                icon="i-lucide-user-check"
                :label="ce('CAPTAIN_EXPERIENCE.OPERATIONS.HANDOFF_PLAN_ACTION')"
                :is-loading="handoffLoadingRunId === item.run_id"
                @click="loadHandoffPlan(item.run_id)"
              />
            </div>
          </div>

          <dl
            v-if="item.currentPlan"
            class="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-5"
          >
            <div
              v-for="row in planSummary(item.currentPlan)"
              :key="row.key"
              class="min-w-0 rounded-lg bg-n-alpha-2 p-3"
            >
              <dt class="text-xs font-medium uppercase text-n-slate-10">
                {{ row.label }}
              </dt>
              <dd class="mt-1 break-words text-sm text-n-slate-12">
                {{ row.value }}
              </dd>
            </div>
          </dl>

          <LaisRedactedField
            v-if="item.currentPlan?.idempotency?.external_key_sha256"
            class="mt-4"
            :label="ce('CAPTAIN_EXPERIENCE.OPERATIONS.REPLAY_SHA_LABEL')"
            :value="item.currentPlan.idempotency.external_key_sha256"
            :reason="ce('CAPTAIN_EXPERIENCE.OPERATIONS.REDACTED_HASH_REASON')"
            redacted
          />

          <LaisPayloadViewer
            v-if="item.currentPlan"
            class="mt-4"
            :title="ce('CAPTAIN_EXPERIENCE.OPERATIONS.REPLAY_PAYLOAD_TITLE')"
            :payload="item.currentPlan"
            :redacted-keys="redactedViewerKeys"
            :can-copy="false"
            :max-depth="2"
          />

          <dl
            v-if="handoffPlansByRunId[item.run_id]"
            class="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4"
          >
            <div
              v-for="row in handoffPlanSummary(
                handoffPlansByRunId[item.run_id]
              )"
              :key="row.key"
              class="min-w-0 rounded-lg bg-n-alpha-2 p-3"
            >
              <dt class="text-xs font-medium uppercase text-n-slate-10">
                {{ row.label }}
              </dt>
              <dd class="mt-1 break-words text-sm text-n-slate-12">
                {{ row.value }}
              </dd>
            </div>
          </dl>

          <LaisPayloadViewer
            v-if="handoffPlansByRunId[item.run_id]"
            class="mt-4"
            :title="ce('CAPTAIN_EXPERIENCE.OPERATIONS.HANDOFF_PAYLOAD_TITLE')"
            :payload="handoffPlansByRunId[item.run_id]"
            :redacted-keys="redactedViewerKeys"
            :can-copy="false"
            :max-depth="2"
          />

          <p
            v-if="replayErrorsByRunId[item.run_id]"
            class="mt-3 text-sm text-n-ruby-11"
          >
            {{ replayErrorsByRunId[item.run_id] }}
          </p>
          <p
            v-if="handoffErrorsByRunId[item.run_id]"
            class="mt-3 text-sm text-n-ruby-11"
          >
            {{ handoffErrorsByRunId[item.run_id] }}
          </p>
        </article>
      </div>
      <p v-else class="text-sm text-n-slate-11">
        {{ ce('CAPTAIN_EXPERIENCE.OPERATIONS.DLQ_EMPTY') }}
      </p>
    </AgentSection>

    <div class="grid gap-6 xl:grid-cols-2">
      <AgentSection
        :title="ce('CAPTAIN_EXPERIENCE.OPERATIONS.CIRCUITS_TITLE')"
        prefix-icon="i-lucide-toggle-left"
      >
        <ul v-if="circuitItems.length" class="flex flex-col gap-2">
          <li
            v-for="item in circuitItems"
            :key="item.key"
            class="flex items-center justify-between gap-3 rounded-lg border border-n-weak bg-n-solid-2 p-3"
          >
            <span class="min-w-0 truncate text-sm text-n-slate-12">
              {{ item.key }}
            </span>
            <LaisStatusBadge
              :label="statusLabel(item.status)"
              :tone="operationStatusTone(item.status)"
              size="sm"
              dot
            />
          </li>
        </ul>
        <p v-else class="text-sm text-n-slate-11">
          {{ ce('CAPTAIN_EXPERIENCE.OPERATIONS.NO_CIRCUITS') }}
        </p>
      </AgentSection>

      <AgentSection
        :title="ce('CAPTAIN_EXPERIENCE.OPERATIONS.ALERTS_TITLE')"
        prefix-icon="i-lucide-bell"
      >
        <ul v-if="alertItems.length" class="flex flex-col gap-2">
          <li
            v-for="item in alertItems"
            :key="item.key"
            class="rounded-lg border border-n-weak bg-n-solid-2 p-3"
          >
            <div class="flex items-center justify-between gap-3">
              <span class="min-w-0 truncate text-sm text-n-slate-12">
                {{ item.key }}
              </span>
              <LaisStatusBadge
                :label="item.severity"
                :tone="operationStatusTone(item.severity)"
                size="sm"
                dot
              />
            </div>
            <p class="mt-1 text-xs text-n-slate-10">
              {{
                ce('CAPTAIN_EXPERIENCE.OPERATIONS.ALERT_RUNBOOK', {
                  runbook: item.runbook_key || '—',
                })
              }}
            </p>
          </li>
        </ul>
        <p v-else class="text-sm text-n-slate-11">
          {{ ce('CAPTAIN_EXPERIENCE.OPERATIONS.NO_ALERTS') }}
        </p>
      </AgentSection>
    </div>

    <AgentSection
      :title="ce('CAPTAIN_EXPERIENCE.OPERATIONS.EVALS_TITLE')"
      prefix-icon="i-lucide-clipboard-check"
    >
      <div class="flex flex-col gap-4">
        <div class="flex flex-wrap items-center gap-2">
          <LaisStatusBadge
            :label="statusLabel(evalStatus)"
            :tone="operationStatusTone(evalStatus)"
            dot
          />
          <span class="text-xs text-n-slate-10">
            {{
              ce('CAPTAIN_EXPERIENCE.OPERATIONS.EVALS_SUMMARY', {
                total: valueAt(snapshot, ['evals', 'total'], '—'),
                failed: valueAt(snapshot, ['evals', 'failed'], '—'),
              })
            }}
          </span>
        </div>
        <ul v-if="evalCheckItems.length" class="flex flex-col gap-2">
          <li
            v-for="item in evalCheckItems"
            :key="item.key"
            class="rounded-lg border border-n-weak bg-n-solid-2 p-3"
          >
            <div class="flex items-center justify-between gap-3">
              <span class="min-w-0 truncate text-sm text-n-slate-12">
                {{ item.key }}
              </span>
              <LaisStatusBadge
                :label="
                  item.passed
                    ? ce('CAPTAIN_EXPERIENCE.OPERATIONS.PASSED')
                    : ce('CAPTAIN_EXPERIENCE.OPERATIONS.FAILED')
                "
                :tone="boolTone(item.passed)"
                size="sm"
                dot
              />
            </div>
            <p v-if="item.detail" class="mt-1 text-xs text-n-slate-10">
              {{ item.detail }}
            </p>
          </li>
        </ul>
      </div>
    </AgentSection>
  </section>
</template>
